import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  HTTPError,
  IChargingCardOrderDTO,
  IChargingCardOrderStatusResponse,
  parseChargingCardOrderDataDTOFromOrderStatusDetailV2,
  PreisIndikation,
  PreisIndikationenApiResponse,
} from '@mwe/models';
import { lastValueFrom } from 'rxjs';
import { AccountLogic } from '../../account/account.logic';
import { EnvironmentService } from '../../environment/environment.service';
import { ChargingCardOrderStore } from '../../order-process/charging-card/charging-card-order.store';

@Injectable({ providedIn: 'root' })
export class ChargingCardOrderService {
  private http = inject(HttpClient);
  private accountLogic = inject(AccountLogic);
  private environmentService = inject(EnvironmentService);
  private chargingCardOrderStore = inject(ChargingCardOrderStore);

  public resourceUrl: string;
  private newClientResourceUrl: string;

  constructor() {
    this.resourceUrl = this.environmentService.getApiUrl() + 'api/chargingcard/';
    this.newClientResourceUrl = this.environmentService.getApiUrl() + 'api/newclient/';
  }

  async confirm(addressCode: string, dataDTO: IChargingCardOrderDTO): Promise<IChargingCardOrderStatusResponse> {
    try {
      const result = await lastValueFrom(
        this.http.post<IChargingCardOrderStatusResponse>(this.getResourceUrl('confirm') + '?addressCode=' + addressCode, dataDTO),
      );
      this.chargingCardOrderStore.updateStoreState({ submitId: result.submitId });
      return result;
    } catch (err) {
      throw new HTTPError(err.status, err.error);
    }
  }

  async retry(submitId: number, dataDTO: IChargingCardOrderDTO): Promise<IChargingCardOrderStatusResponse> {
    try {
      return await lastValueFrom(
        this.http.put<IChargingCardOrderStatusResponse>(this.getResourceUrl('retry') + '?submitId=' + submitId, dataDTO),
      );
    } catch (err) {
      throw new HTTPError(err.status, err.error);
    }
  }

  async getPriceIndications(): Promise<PreisIndikation[]> {
    try {
      const params = { plz: '', sparte: 'tanke' };
      const response = await lastValueFrom(
        this.http.get<PreisIndikationenApiResponse>(this.getNewClientResourceUrl('preisindikationen'), { params }),
      );
      return response?.preisindikationen ?? [];
    } catch (err) {
      throw new HTTPError(err.status, err.error);
    }
  }

  confirmStatus(submitId: number): Promise<IChargingCardOrderStatusResponse> {
    const url = this.getResourceUrl('confirmStatus') + '?submitId=' + submitId;
    return lastValueFrom(this.http.get<IChargingCardOrderStatusResponse>(url));
  }

  async getOrderStatusDetails(): Promise<IChargingCardOrderDTO> {
    const response = await lastValueFrom(
      this.http.get(this.resourceUrl + 'orderStatusDetails/' + this.chargingCardOrderStore.lastOrderID()),
    );

    const result = parseChargingCardOrderDataDTOFromOrderStatusDetailV2(response);
    const mappingData = result.mappingData[0];
    const priceIndication = result.priceIndications[0];
    this.chargingCardOrderStore.updateStoreState({
      invoiceAddress: result.newAddress,
      orderDate: result.registrationDate,
      personalData: result.personalData,
      bankDetails: result.sepaMandat,
      eRechnungsInfo: result.eRechnungsInfo,
      productSelection: {
        emobility: {
          sparte: mappingData.categories[0],
          onlineTarif: true,
          kundenType: null,
          tarif: {
            ...priceIndication?.tarif,
            tarifKey: priceIndication?.tarif?.ISUTarifKey,
          },
        },
      },
      carModelType: result.carModelType,
      businessData: result.businessData,
      numberOfCards: result.numberOfCards,
    });

    return result;
  }

  private getResourceUrl(path?: string): string {
    let baseUrl = this.resourceUrl;
    if (this.accountLogic.isLoggedOut()) {
      baseUrl = this.resourceUrl.replace('api', 'api/public');
    }
    return baseUrl + (path ? path : '');
  }

  private getNewClientResourceUrl(path?: string): string {
    let baseUrl = this.newClientResourceUrl;
    if (this.accountLogic.isLoggedOut()) {
      baseUrl = this.newClientResourceUrl.replace('api', 'api/public');
    }
    return baseUrl + (path ? path : '');
  }
}
